import React, { Component, useContext } from 'react';
import PropTypes from 'prop-types';
import { RuntimeContext } from '../../../../../../common/RuntimeContext';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { reduxForm, Field, getFormValues, change, isDirty } from 'redux-form';
import { Form } from 'react-bootstrap';
import BasicToggle from '../../../../../shared/components/formComponents/groups/BasicToggle';
import VerbButton, { VERB_BUTTON_TYPE } from '../../../../../shared/components/VerbButton';
import FormFeedback from '../../../../../shared/components/formComponents/FormFeedback';
import PageHeader from '../../../../../shared/components/PageHeader';
import VerbParagraph from '../../../../../shared/components/VerbParagraph';
import { DEFAULT_IMAGE_URLS } from '../../../../../shared/Constants';

class CompanyNotificationForm extends Component {
    static propTypes = {
        currentValues: PropTypes.object.isRequired,
        handleSubmit: PropTypes.func,
        onCancel: PropTypes.func,
        submitting: PropTypes.bool,
        error: PropTypes.string,
        changeFormValue: PropTypes.func,
        dirty: PropTypes.bool,
        toggleShowUnsavedChangesPrompt: PropTypes.func,
        loggedInUser: PropTypes.object.isRequired,
        slackToken: PropTypes.object
    };

    static contextType = RuntimeContext;

    componentDidUpdate(prevProps) {
        if (prevProps.dirty !== this.props.dirty) {
            return this.props.toggleShowUnsavedChangesPrompt(this.props.dirty);
        }
        return null;
    }

    _renderSlackButton() {
        const { loggedInUser, slackToken } = this.props;

        const slackUrl = `${this.context.pathsWeb.SLACK_URL}&state=${loggedInUser._id}&redirect_uri=${encodeURI(
            `${this.context.pathsWeb.adminUrl}/slack_callback`
        )}`;

        let description =
            'Add Verb to your Slack workspace to allow your team to receive reminders about content, upcoming due activity, and more.';
        let buttonId = 'ADD_TO_SLACK';
        let buttonText = 'ADD TO SLACK';
        let imageUrl = DEFAULT_IMAGE_URLS.SLACK_LOGO_WHITE;
        let imageDimensions = { w: 35 };
        let buttonClass = 'slack-btn';
        let buttonClick = () => {
            window.location.replace(slackUrl);
        };

        if (slackToken) {
            description =
                'Verb is already added to your Slack workspace. (Visit your Slack App Directory if you wish to remove Verb from your account.)';
            buttonId = 'REMOVE_SLACK';
            buttonText = 'REMOVE SLACK';
            imageUrl = null;
            imageDimensions = null;
            buttonClass = '';
            buttonClick = () =>
                window.open(
                    'https://slack.com/help/articles/360003125231-Remove-apps-and-custom-integrations-from-your-workspace#remove-an-app'
                );
        }

        return (
            <div className={'row'}>
                <div className={'col-md-6 company-form-field'}>
                    <p className={'form-description'}>{description}</p>
                </div>
                <div className={'slack-button'}>
                    <VerbButton
                        textId={buttonId}
                        defaultMessage={buttonText}
                        className={buttonClass}
                        imageUrl={imageUrl}
                        imageDimensions={imageDimensions}
                        onClick={buttonClick}
                    />
                </div>
            </div>
        );
    }

    render() {
        const { currentValues = {}, handleSubmit, changeFormValue, submitting, onCancel, error } = this.props;

        return (
            <>
                <VerbParagraph
                    className={'col-7'}
                    messageId={'COMPANY_NOTIFICATIONS_USER'}
                    defaultMessage={
                        'These settings establish the default notifications that your employees receive. Employees have the ability to unsubscribe from categories of email notifications (team activity, reminders, content suggestions, co-worker recommendations).'
                    }
                />
                <Form className={'companyForm container'}>
                    <Field
                        name={'emailEnabled'}
                        className={'row company-form-field notifications'}
                        inputClassName={'col-12 col-sm-6'}
                        label={'Notifications'}
                        description={'Receive notifications for email or Slack (if added to workspace)'}
                        component={BasicToggle}
                        styleProps={{ horizontal: true }}
                        onChange={(value) => {
                            changeFormValue({ field: 'emailConfigs.peerRecommendation', value });
                            changeFormValue({ field: 'emailConfigs.teamLeadRecommendation', value });
                            changeFormValue({ field: 'emailConfigs.teamLeadAnnoucement', value });
                            changeFormValue({ field: 'emailConfigs.teamActivity', value });
                            changeFormValue({ field: 'emailConfigs.teamFeedPost', value });
                            changeFormValue({ field: 'emailConfigs.newContent', value });
                            changeFormValue({ field: 'emailConfigs.collectionReminder', value });
                            changeFormValue({ field: 'emailConfigs.discover', value });
                            changeFormValue({ field: 'emailConfigs.dueDateReminders', value });
                        }}
                        id={'company-notifications-email-enabled-toggle'}
                    />
                    {currentValues.emailEnabled ? (
                        <>
                            <Field
                                name={'emailConfigs.peerRecommendation'}
                                className={'row company-form-field secondary-field'}
                                inputClassName={'col-12 col-sm-6'}
                                label={'Coworker Recommendations'}
                                description={'Sent whenever a coworker recommends a collection on Verb.'}
                                component={BasicToggle}
                                styleProps={{ horizontal: true }}
                                id={'company-notifications-peer-recommendations-toggle'}
                            />

                            <Field
                                name={'emailConfigs.teamLeadRecommendation'}
                                className={'row company-form-field'}
                                inputClassName={'col-12 col-sm-6'}
                                label={'Team Lead Recommendations and Assignments'}
                                description={
                                    'Sent to all team members when a Team Lead recommends or assigns a collection on a Team page.'
                                }
                                component={BasicToggle}
                                styleProps={{ horizontal: true }}
                                id={'company-notifications-team-lead-recommendations-toggle'}
                            />

                            <Field
                                name={'emailConfigs.teamLeadAnnoucement'}
                                className={'row company-form-field'}
                                inputClassName={'col-12 col-sm-6'}
                                label={'Team Lead Announcements'}
                                description={
                                    'Sent to all team members when a Team Lead or Company Admin sends out an announcement on a Team page.'
                                }
                                component={BasicToggle}
                                styleProps={{ horizontal: true }}
                                id={'company-notifications-team-lead-announcements-toggle'}
                            />

                            <Field
                                name={'emailConfigs.teamFeedPost'}
                                className={'row company-form-field'}
                                inputClassName={'col-12 col-sm-6'}
                                label={'Team Feed Post'}
                                description={
                                    'Sent to all members of a team whenever someone makes a new post on a Team page.'
                                }
                                component={BasicToggle}
                                styleProps={{ horizontal: true }}
                                id={'company-notifications-team-feed-post-toggle'}
                            />

                            <Field
                                name={'emailConfigs.teamActivity'}
                                className={'row company-form-field'}
                                inputClassName={'col-12 col-sm-6'}
                                label={'Team Feed Replies'}
                                description={
                                    'Sent to learners whenever someone comments on their actions or replies to a post they made on a Team page.'
                                }
                                component={BasicToggle}
                                styleProps={{ horizontal: true }}
                                id={'company-notifications-team-activity-toggle'}
                            />

                            <Field
                                name={'emailConfigs.collectionReminder'}
                                className={'row company-form-field'}
                                inputClassName={'col-12 col-sm-6'}
                                label={'Collection Reminder'}
                                description={
                                    'Sent after 14 days of inactivity to learners who have started but not completed an activity.'
                                }
                                component={BasicToggle}
                                styleProps={{ horizontal: true }}
                                id={'company-notifications-collection-reminder-toggle'}
                            />
                            <Field
                                name={'emailConfigs.discover'}
                                className={'row company-form-field'}
                                inputClassName={'col-12 col-sm-6'}
                                label={'Discover'}
                                description={'Nudges users to return to Verb 7 days after completing a collection.'}
                                component={BasicToggle}
                                styleProps={{ horizontal: true }}
                                id={'company-notifications-discover-toggle'}
                            />
                            <Field
                                name={'emailConfigs.newContent'}
                                className={'row company-form-field'}
                                inputClassName={'col-12 col-sm-6'}
                                label={'New Activity Published in Collection'}
                                description={
                                    'Sent to users enrolled in a collection when a new activity is added to that collection.'
                                }
                                component={BasicToggle}
                                styleProps={{ horizontal: true }}
                                id={'company-notifications-new-content-toggle'}
                            />

                            <Field
                                name={'emailConfigs.dueDateReminders'}
                                className={'row company-form-field'}
                                inputClassName={'col-12 col-sm-6'}
                                label={'Due date reminders'}
                                description={
                                    'Sent to learners the day before an assigned collection, pathway collection milestone, pathway event milestone, or pathway task milestone is due.'
                                }
                                component={BasicToggle}
                                styleProps={{ horizontal: true }}
                                id={'company-notifications-due-date-reminder-toggle'}
                            />
                        </>
                    ) : null}
                    <div className={'col-12 formButtons'}>
                        <VerbButton
                            fontAwesomeIcon={'fa-save'}
                            textId={'BUTTON_SAVE'}
                            defaultMessage={'SAVE'}
                            onClick={handleSubmit}
                            disabled={submitting}
                        />
                        <VerbButton
                            buttonType={VERB_BUTTON_TYPE.SECONDARY_DEFAULT}
                            fontAwesomeIcon={'fa-ban'}
                            textId={'BUTTON_CANCEL'}
                            defaultMessage={'CANCEL'}
                            onClick={onCancel}
                            disabled={submitting}
                        />
                    </div>
                    <FormFeedback failMsg={error} className={'col-12'} {...this.props} />
                </Form>
                <div className={'enable-slack-notifications'}>
                    <PageHeader
                        title={'Enable Slack Notifications'}
                        bottomBorder={true}
                        hideBackButton={true}
                        className={'company-form-header'}
                    />
                    {this._renderSlackButton()}
                </div>
            </>
        );
    }
}

export const CompanyNotificationFormComponent = CompanyNotificationForm;

export const makeMapStateToProps = () => {
    const mapStateToProps = (state, ownProps) => {
        const { emailEnabled, emailConfigs = {}, slackToken } = ownProps.company;
        const currentValues = getFormValues('CompanyNotification')(state);
        return {
            dirty: isDirty('CompanyNotifications'),
            initialValues: {
                emailEnabled,
                emailConfigs: {
                    peerRecommendation: emailConfigs.peerRecommendation,
                    teamLeadRecommendation: emailConfigs.teamLeadRecommendation,
                    teamLeadAnnoucement: emailConfigs.teamLeadAnnoucement,
                    teamFeedPost: emailConfigs.teamFeedPost,
                    teamActivity: emailConfigs.teamActivity,
                    newContent: emailConfigs.newContent,
                    collectionReminder: emailConfigs.collectionReminder,
                    discover: emailConfigs.discover,
                    dueDateReminders: emailConfigs.dueDateReminders
                }
            },
            slackToken,
            currentValues
        };
    };

    return mapStateToProps;
};
const mapDispatchToProps = (dispatch) => ({
    changeFormValue: (params) => dispatch(change('CompanyNotification', params.field, params.value))
});
const CompanyInfoFormValidation = {
    form: 'CompanyNotification',
    destroyOnUnmount: false,
    enableReinitialize: true
};

export default compose(
    connect(makeMapStateToProps, mapDispatchToProps),
    reduxForm(CompanyInfoFormValidation)
)(CompanyNotificationForm);
